import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import {
  useAdminScope,
} from "../../../context/AdminScopeContext";

import {
  AdminButton,
} from "../../../pages/admin/action_engine";

import {
  AdminSection,
  AdminSectionHeader,
  AdminSectionTitle,
} from "../../../pages/admin/section_engine";

import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../../pages/admin/ui_engine";

import {
  createSupplierCapability,
  getSupplierCapabilityErrorMessage,
  listSupplierCapabilities,
  updateSupplierCapability,
} from "../../../services/supplierCapabilitiesAdminApi";

import SupplierCapabilityForm
  from "./SupplierCapabilityForm";


function normalizeSupplierUuid(
  supplierUuid,
) {
  if (
    typeof supplierUuid !== "string"
  ) {
    return "";
  }

  return supplierUuid.trim();
}


function capabilityKey(
  capability,
  index,
) {
  return (
    capability?.uuid
    || `${capability?.channel || "capability"}-${index}`
  );
}


export default function SupplierCapabilitiesWorkspace({
  supplierUuid,
  supplierName = "",
}) {
  const {
    selectedCompanyUuid,
    selectedActivityUuid,
  } = useAdminScope();

  const normalizedSupplierUuid =
    useMemo(
      () => normalizeSupplierUuid(
        supplierUuid,
      ),
      [supplierUuid],
    );

  const [capabilities, setCapabilities] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [editingCapability, setEditingCapability] =
    useState(null);

  const [formOpen, setFormOpen] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);


  const scope = useMemo(
    () => ({
      companyUuid:
        selectedCompanyUuid,
      activityUuid:
        selectedActivityUuid,
    }),
    [
      selectedActivityUuid,
      selectedCompanyUuid,
    ],
  );


  const loadCapabilities =
    useCallback(
      async () => {
        if (!normalizedSupplierUuid) {
          setCapabilities([]);
          setError("");
          return;
        }

        setLoading(true);
        setError("");

        try {
          const items =
            await listSupplierCapabilities(
              normalizedSupplierUuid,
              scope,
            );

          setCapabilities(
            Array.isArray(items)
              ? items
              : [],
          );
        } catch (requestError) {
          setCapabilities([]);

          setError(
            getSupplierCapabilityErrorMessage(
              requestError,
              "Impossible de charger les capacités fournisseur.",
            ),
          );
        } finally {
          setLoading(false);
        }
      },
      [
        normalizedSupplierUuid,
        scope,
      ],
    );


  useEffect(() => {
    loadCapabilities();
  }, [loadCapabilities]);


  const openCreate = () => {
    setEditingCapability(null);
    setFormOpen(true);
  };


  const openEdit = (
    capability,
  ) => {
    setEditingCapability(
      capability,
    );

    setFormOpen(true);
  };


  const closeForm = () => {
    if (submitting) {
      return;
    }

    setEditingCapability(null);
    setFormOpen(false);
  };


  const handleSubmit =
    async (payload) => {
      if (!normalizedSupplierUuid) {
        return;
      }

      setSubmitting(true);
      setError("");

      try {
        if (editingCapability?.uuid) {
          await updateSupplierCapability(
            normalizedSupplierUuid,
            editingCapability.uuid,
            payload,
            scope,
          );
        } else {
          await createSupplierCapability(
            normalizedSupplierUuid,
            payload,
            scope,
          );
        }

        setEditingCapability(null);
        setFormOpen(false);

        await loadCapabilities();
      } catch (requestError) {
        setError(
          getSupplierCapabilityErrorMessage(
            requestError,
            "Impossible d'enregistrer la capacité fournisseur.",
          ),
        );
      } finally {
        setSubmitting(false);
      }
    };


  if (!normalizedSupplierUuid) {
    return null;
  }


  return (
    <AdminSection
      data-testid="supplier-capabilities-workspace"
      data-supplier-uuid={
        normalizedSupplierUuid
      }
    >
      <AdminSectionHeader>
        <HeaderContent>
          <div>
            <AdminSectionTitle>
              Capacités fournisseur
            </AdminSectionTitle>

            {supplierName && (
              <SupplierName>
                {supplierName}
              </SupplierName>
            )}
          </div>

          <AdminButton
            type="button"
            variant="primary"
            onClick={openCreate}
          >
            Ajouter une capacité
          </AdminButton>
        </HeaderContent>
      </AdminSectionHeader>

      {error && (
        <StateWrapper
          data-testid="supplier-capabilities-error"
        >
          <AdminErrorState />
          <StateMessage>
            {error}
          </StateMessage>
        </StateWrapper>
      )}

      {loading && (
        <StateWrapper
          data-testid="supplier-capabilities-loading"
        >
          <AdminLoadingState />
        </StateWrapper>
      )}

      {!loading
        && capabilities.length === 0
        && !formOpen
        && (
          <StateWrapper
            data-testid="supplier-capabilities-empty"
          >
            <AdminEmptyState />
            <StateMessage>
              Aucune capacité configurée
              pour ce fournisseur.
            </StateMessage>
          </StateWrapper>
        )}

      {!loading
        && capabilities.length > 0
        && (
          <CapabilityList
            data-testid="supplier-capabilities-list"
          >
            {capabilities.map(
              (
                capability,
                index,
              ) => (
                <CapabilityCard
                  key={capabilityKey(
                    capability,
                    index,
                  )}
                >
                  <CapabilitySummary>
                    <strong>
                      {String(
                        capability.channel
                        || "",
                      ).toUpperCase()}
                    </strong>

                    <span>
                      {(
                        capability.supported_formats
                        || []
                      ).join(", ")
                        || "Aucun format"}
                    </span>

                    <Badges>
                      {capability.is_current && (
                        <Badge>
                          actuel
                        </Badge>
                      )}

                      {capability.is_target && (
                        <Badge>
                          cible
                        </Badge>
                      )}

                      {capability.is_preferred && (
                        <Badge>
                          préféré
                        </Badge>
                      )}
                    </Badges>

                    <StatusLine>
                      Qualification :
                      {" "}
                      {
                        capability.qualification_status
                        || "unknown"
                      }
                    </StatusLine>

                    <StatusLine>
                      Certification :
                      {" "}
                      {
                        capability.certification_status
                        || "uncertified"
                      }
                    </StatusLine>

                    {capability.fallback_priority
                      != null && (
                        <StatusLine>
                          Priorité fallback :
                          {" "}
                          {
                            capability.fallback_priority
                          }
                        </StatusLine>
                      )}
                  </CapabilitySummary>

                  <AdminButton
                    type="button"
                    variant="ghost"
                    size="small"
                    onClick={() => {
                      openEdit(
                        capability,
                      );
                    }}
                  >
                    Modifier
                  </AdminButton>
                </CapabilityCard>
              ),
            )}
          </CapabilityList>
        )}

      {formOpen && (
        <SupplierCapabilityForm
          capability={
            editingCapability
          }
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}
    </AdminSection>
  );
}


const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const SupplierName = styled.div`
  margin-top: 4px;
  opacity: 0.75;
`;

const StateWrapper = styled.div`
  padding: 16px 0;
`;

const StateMessage = styled.p`
  margin: 8px 0 0;
`;

const CapabilityList = styled.div`
  display: grid;
  gap: 10px;
`;

const CapabilityCard = styled.article`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 12px;
  border: 1px solid
    rgba(127, 127, 127, 0.25);
  border-radius: 8px;
`;

const CapabilitySummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  font-size: 0.8rem;
  padding: 2px 6px;
  border-radius: 999px;
  background:
    rgba(127, 127, 127, 0.15);
`;

const StatusLine = styled.span`
  font-size: 0.9rem;
  opacity: 0.8;
`;
